import React, { useState } from 'react';

const SECTIONS = [
  {
    id: 'quick',
    title: '⚡ Quick Fix Commands',
    color: 'bg-blue-50 border-blue-200',
    headerColor: 'bg-blue-600',
    commands: [
      {
        title: 'Full Rebuild (All containers)',
        desc: 'Use when you\'ve made any code changes. Rebuilds frontend and backend.',
        code: `cd /opt/reception-app
docker compose up --build -d`
      },
      {
        title: 'Fix npm ci Error (Dockerfile patch)',
        desc: 'Run once to fix "npm ci requires package-lock.json" error in Dockerfiles.',
        code: `cd /opt/reception-app
sed -i 's/RUN npm ci$/RUN npm install/' frontend/Dockerfile
sed -i 's/RUN npm ci --only=production/RUN npm install --only=production/' backend/Dockerfile
docker compose up --build -d`
      },
      {
        title: 'Rebuild Frontend Only',
        desc: 'Use when you only changed frontend files or environment variables.',
        code: `cd /opt/reception-app
docker compose up --build -d frontend`
      },
      {
        title: 'Rebuild Backend Only',
        desc: 'Use when you only changed backend files.',
        code: `cd /opt/reception-app
docker compose up --build -d backend`
      },
    ]
  },
  {
    id: 'login',
    title: '🔐 Login / Auth Issues',
    color: 'bg-yellow-50 border-yellow-200',
    headerColor: 'bg-yellow-600',
    commands: [
      {
        title: 'Fix Login Failure — Set Correct HTTPS Domain',
        desc: 'When browser shows CORS error or "Login failed" on https://reception.shahzadtex.com',
        code: `cd /opt/reception-app

# Fix .env file
sed -i 's|REACT_APP_API_URL=.*|REACT_APP_API_URL=https://reception.shahzadtex.com|' .env
sed -i 's|FRONTEND_URL=.*|FRONTEND_URL=https://reception.shahzadtex.com|' .env

# Fix docker-compose default fallback
sed -i 's|REACT_APP_API_URL:-http://[^}]*|REACT_APP_API_URL:-https://reception.shahzadtex.com|' docker-compose.yml
sed -i 's|FRONTEND_URL:-http://[^}]*|FRONTEND_URL:-https://reception.shahzadtex.com|' docker-compose.yml

# Verify changes
grep -E "REACT_APP_API_URL|FRONTEND_URL" .env docker-compose.yml

# Rebuild frontend + restart backend
docker compose up --build -d`
      },
      {
        title: 'Reset Admin Password to "admin"',
        desc: 'If you cannot log in with admin user, run this to reset password.',
        code: `docker exec reception_backend node -e "
const bcrypt = require('bcrypt');
const { Pool } = require('pg');
const pool = new Pool({
  host: process.env.DB_HOST,
  port: process.env.DB_PORT,
  database: process.env.DB_NAME,
  user: process.env.DB_USER,
  password: process.env.DB_PASSWORD
});
bcrypt.hash('admin', 10).then(hash => {
  pool.query(
    'UPDATE users SET password=\\$1 WHERE username=\\'admin\\'',
    [hash]
  ).then(() => { console.log('Password reset to: admin'); pool.end(); });
});
"`
      },
      {
        title: 'Restart Backend Only (No Rebuild)',
        desc: 'Forces backend to re-read .env file without full rebuild.',
        code: `cd /opt/reception-app
docker compose up -d --no-build --force-recreate backend`
      },
    ]
  },
  {
    id: 'database',
    title: '🗄️ Database Commands',
    color: 'bg-green-50 border-green-200',
    headerColor: 'bg-green-600',
    commands: [
      {
        title: 'Run Database Migration',
        desc: 'Apply schema updates (migration_v2.sql, migration_v3.sql) to existing database.',
        code: `cd /opt/reception-app

# Run V2 migration (adds multi-company, vehicles, couriers, etc.)
docker exec -i reception_db psql -U reception_user -d reception_db < database/migration_v2.sql

# Run V3 migration (adds company isolation, guest houses, etc.)
docker exec -i reception_db psql -U reception_user -d reception_db < database/migration_v3.sql`
      },
      {
        title: 'Connect to Database (psql shell)',
        desc: 'Open interactive SQL prompt inside the database container.',
        code: `docker exec -it reception_db psql -U reception_user -d reception_db

# Inside psql:
# \\dt             — list all tables
# \\d employees    — describe employees table
# \\q              — quit`
      },
      {
        title: 'Check Database Size',
        desc: 'See how much data is in each table.',
        code: `docker exec reception_db psql -U reception_user -d reception_db -c "
SELECT schemaname, tablename,
  pg_size_pretty(pg_total_relation_size(schemaname||'.'||tablename)) AS size
FROM pg_tables
WHERE schemaname = 'public'
ORDER BY pg_total_relation_size(schemaname||'.'||tablename) DESC;
"`
      },
      {
        title: 'Backup Database',
        desc: 'Create a full database backup file.',
        code: `docker exec reception_db pg_dump -U reception_user reception_db > /opt/reception-app/backup_$(date +%Y%m%d_%H%M%S).sql
echo "Backup complete"`
      },
      {
        title: 'Restore Database Backup',
        desc: 'Restore from a .sql backup file. WARNING: This will overwrite existing data.',
        code: `# First, identify your backup file
ls -la /opt/reception-app/*.sql

# Restore (replace backup_YYYYMMDD_HHMMSS.sql with actual filename)
docker exec -i reception_db psql -U reception_user -d reception_db < /opt/reception-app/backup_YYYYMMDD_HHMMSS.sql`
      },
    ]
  },
  {
    id: 'containers',
    title: '🐳 Container Management',
    color: 'bg-purple-50 border-purple-200',
    headerColor: 'bg-purple-600',
    commands: [
      {
        title: 'View All Container Status',
        desc: 'Check if all 3 containers (db, backend, frontend) are running.',
        code: `docker ps
docker compose ps`
      },
      {
        title: 'View Live Logs',
        desc: 'Watch real-time logs to debug errors.',
        code: `# All containers
docker compose logs -f

# Backend only
docker compose logs -f backend

# Database only
docker compose logs -f db`
      },
      {
        title: 'Stop All Containers',
        desc: 'Gracefully stop the application.',
        code: `cd /opt/reception-app
docker compose down`
      },
      {
        title: 'Start All Containers',
        desc: 'Start previously stopped containers.',
        code: `cd /opt/reception-app
docker compose up -d`
      },
      {
        title: 'Hard Reset (Clean Rebuild)',
        desc: 'Remove containers and volumes then rebuild from scratch. ⚠️ This deletes the database!',
        code: `cd /opt/reception-app
# WARNING: This deletes ALL data!
docker compose down -v
docker compose up --build -d`
      },
    ]
  },
  {
    id: 'deploy',
    title: '🚀 Deploy Updates from Local',
    color: 'bg-orange-50 border-orange-200',
    headerColor: 'bg-orange-600',
    commands: [
      {
        title: 'Transfer Files to VPS (from local Mac)',
        desc: 'Run this on your local Mac to upload code changes to VPS.',
        code: `# Run on your LOCAL Mac, not VPS
rsync -avz --exclude 'node_modules' --exclude '.git' \
  "/Users/macmini/Documents/Claude Coworker/Reception WebApp/" \
  root@76.13.17.186:/opt/reception-app/`
      },
      {
        title: 'Full Deploy After rsync',
        desc: 'After transferring files, run these on the VPS to apply changes.',
        code: `cd /opt/reception-app

# Fix Dockerfile if needed
sed -i 's/RUN npm ci$/RUN npm install/' frontend/Dockerfile
sed -i 's/RUN npm ci --only=production/RUN npm install --only=production/' backend/Dockerfile

# Apply any database migrations
docker exec -i reception_db psql -U reception_user -d reception_db < database/migration_v3.sql

# Rebuild and restart
docker compose up --build -d

# Check everything is running
docker ps`
      },
      {
        title: 'Check Current .env Settings',
        desc: 'Verify your environment variables are correct.',
        code: `cat /opt/reception-app/.env
echo "--- docker-compose defaults ---"
grep -E "REACT_APP_API_URL|FRONTEND_URL|DB_|JWT" /opt/reception-app/docker-compose.yml`
      },
    ]
  },
  {
    id: 'ssl',
    title: '🔒 SSL / HTTPS Issues',
    color: 'bg-red-50 border-red-200',
    headerColor: 'bg-red-600',
    commands: [
      {
        title: 'Fix Mixed Content Error (HTTPS → HTTP blocked)',
        desc: 'When site loads on HTTPS but API calls are blocked. API must also use HTTPS.',
        code: `cd /opt/reception-app

# Set HTTPS URL in .env
echo "REACT_APP_API_URL=https://reception.shahzadtex.com" >> .env
echo "FRONTEND_URL=https://reception.shahzadtex.com" >> .env

# Or edit .env manually
nano /opt/reception-app/.env

# Then rebuild frontend
docker compose up --build -d frontend`
      },
      {
        title: 'Check nginx SSL Config',
        desc: 'Verify nginx is properly proxying API requests.',
        code: `# Check nginx config on host (if nginx is on host, not in container)
cat /etc/nginx/sites-enabled/reception.shahzadtex.com

# Or check the frontend container's nginx config
docker exec reception_frontend cat /etc/nginx/conf.d/default.conf`
      },
    ]
  },
];

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button onClick={copy}
      className={`text-xs px-3 py-1 rounded font-medium transition-all ${
        copied ? 'bg-green-500 text-white' : 'bg-gray-700 hover:bg-gray-600 text-gray-200'
      }`}>
      {copied ? '✅ Copied!' : '📋 Copy'}
    </button>
  );
}

export default function AdminHelp() {
  const [openSection, setOpenSection] = useState('quick');

  return (
    <div className="space-y-5 max-w-5xl">
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl p-6 text-white">
        <h1 className="text-2xl font-bold mb-1">🛠️ Admin Help & Commands</h1>
        <p className="text-gray-300 text-sm">
          Docker commands, database management, and troubleshooting for
          Reception Management System on VPS (76.13.17.186)
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <span className="bg-gray-700 text-gray-200 text-xs px-3 py-1 rounded-full">Server: 76.13.17.186</span>
          <span className="bg-gray-700 text-gray-200 text-xs px-3 py-1 rounded-full">App: /opt/reception-app</span>
          <span className="bg-gray-700 text-gray-200 text-xs px-3 py-1 rounded-full">Frontend: :3008</span>
          <span className="bg-gray-700 text-gray-200 text-xs px-3 py-1 rounded-full">Backend: :8009</span>
          <span className="bg-gray-700 text-gray-200 text-xs px-3 py-1 rounded-full">DB: :5434</span>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="flex flex-wrap gap-2">
        {SECTIONS.map(s => (
          <button key={s.id}
            onClick={() => setOpenSection(openSection === s.id ? null : s.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border ${
              openSection === s.id
                ? 'bg-gray-800 text-white border-gray-800'
                : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'
            }`}>
            {s.title}
          </button>
        ))}
      </div>

      {/* Sections */}
      {SECTIONS.map(section => (
        openSection === section.id && (
          <div key={section.id} className="space-y-4">
            {section.commands.map((cmd, idx) => (
              <div key={idx} className={`rounded-xl border-2 ${section.color} overflow-hidden`}>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <h3 className="font-semibold text-gray-800">{cmd.title}</h3>
                      <p className="text-sm text-gray-500 mt-0.5">{cmd.desc}</p>
                    </div>
                    <CopyButton text={cmd.code} />
                  </div>
                  <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 text-xs overflow-x-auto leading-relaxed whitespace-pre">
{cmd.code}
                  </pre>
                </div>
              </div>
            ))}
          </div>
        )
      ))}

      {/* Quick Reference */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <h2 className="font-bold text-gray-800 mb-4">📊 Quick Reference</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <h3 className="font-semibold text-gray-700 mb-2">Container Names</h3>
            <div className="space-y-1 font-mono text-xs bg-gray-50 rounded-lg p-3">
              <p><span className="text-blue-600">reception_frontend</span> — nginx serving React</p>
              <p><span className="text-green-600">reception_backend</span> — Node.js API</p>
              <p><span className="text-purple-600">reception_db</span> — PostgreSQL</p>
            </div>
          </div>
          <div>
            <h3 className="font-semibold text-gray-700 mb-2">Default Credentials</h3>
            <div className="space-y-1 font-mono text-xs bg-gray-50 rounded-lg p-3">
              <p>Admin: <span className="text-blue-600">admin / admin</span></p>
              <p>DB User: <span className="text-blue-600">reception_user</span></p>
              <p>DB Name: <span className="text-blue-600">reception_db</span></p>
            </div>
          </div>
          <div>
            <h3 className="font-semibold text-gray-700 mb-2">Key Files on VPS</h3>
            <div className="space-y-1 font-mono text-xs bg-gray-50 rounded-lg p-3">
              <p>/opt/reception-app/.env</p>
              <p>/opt/reception-app/docker-compose.yml</p>
              <p>/opt/reception-app/database/migration_v3.sql</p>
            </div>
          </div>
          <div>
            <h3 className="font-semibold text-gray-700 mb-2">Common docker compose Commands</h3>
            <div className="space-y-1 font-mono text-xs bg-gray-50 rounded-lg p-3">
              <p>docker compose ps</p>
              <p>docker compose logs -f backend</p>
              <p>docker compose restart backend</p>
              <p>docker compose exec backend sh</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
